import type { QueryClient } from "@tanstack/react-query";
import { crossPostingQueryKeys } from "./queryKeys";

/**
 * Invalidate cross-posting query caches by scope, after a mutation lands.
 *
 * Implements CONTEXT.md D-05. Every mutation hook's `onSuccess` calls this
 * helper with the narrowest sensible scope so consumers that don't import
 * it don't pay for bulk invalidation.
 *
 * Scope semantics (React Query v5 prefix-match on array queryKey):
 *   - "profile"   → socialProfile(comedianId)
 *   - "history"   → any ["crossPostHistory", comedianId, …] (every page/pageSize variant)
 *   - "all" (default) → profile + history
 */
export async function invalidateCrossPosting(
  queryClient: QueryClient,
  target: {
    comedianId: string;
    scope?: "all" | "history" | "profile";
  }
): Promise<void> {
  const scope = target.scope ?? "all";
  const promises: Promise<void>[] = [];

  if (scope === "all" || scope === "profile") {
    promises.push(
      queryClient.invalidateQueries({
        queryKey: crossPostingQueryKeys.socialProfile(target.comedianId),
      })
    );
  }

  if (scope === "all" || scope === "history") {
    // Partial match: invalidates every ["crossPostHistory", comedianId, …] entry
    // (every page/pageSize variant).
    promises.push(
      queryClient.invalidateQueries({
        queryKey: ["crossPostHistory", target.comedianId],
      })
    );
  }

  await Promise.all(promises);
}
